import React from "react";
import { Link } from "react-router";

import { ColumnDef } from "@tanstack/react-table";

import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { ShowButton } from "@/components/refine-ui/buttons/show";
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import { ITeamData } from "@/types/team";

const options = (...values: string[]) =>
  values.map((value) => ({ label: value, value }));

const enumColumn = (
  id: string,
  header: string,
  key: string,
  opts: { label: string; value: string }[],
  filterType: "faceted-select" | "select" = "faceted-select",
): ColumnDef<ITeamData> => ({
  id,
  header,
  accessorKey: `document.${key}`,
  enableSorting: false,
  size: 120,
  meta: { filterType, options: opts },
});

const booleanOptions = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

export const useTeamColumns = () =>
  React.useMemo<ColumnDef<ITeamData>[]>(
    () => [
      { id: "id", accessorKey: "document.id", header: "ID" },
      {
        id: "logo",
        header: "",
        accessorFn: (row) => row.document.media?.photos?.[0],
        enableSorting: false,
        enableColumnFilter: false,
        size: 56,
        cell: function render({ getValue }: any) {
          const photo = getValue();
          return photo?.url ? (
            <PhotoWithBlurhash
              className="size-8 rounded object-contain"
              url={photo.url}
              blurhash={photo.blurhash ?? ""}
            />
          ) : (
            <div className="size-8 rounded bg-muted" />
          );
        },
      },
      {
        id: "name",
        header: "Name",
        minSize: 200,
        size: 280,
        accessorFn: (row) =>
          row.highlights?.filter((h: any) => h.field === "name")[0]?.snippet ??
          row.document.name,
        cell: function render({ row, getValue }: any) {
          return makeTooltip(
            <Link
              to={`/teams/show/${row.original.document.id}`}
              className="block truncate font-medium hover:underline"
              dangerouslySetInnerHTML={{ __html: getValue() }}
            />,
          );
        },
        enableSorting: false,
        meta: { filterType: "text" },
      },
      {
        id: "shortName",
        header: "Short name",
        accessorKey: "document.shortName",
        enableSorting: false,
        size: 120,
        meta: { filterType: "text" },
      },
      enumColumn(
        "sport",
        "Sport",
        "sport",
        options("Football", "Basketball", "Volleyball"),
      ),
      {
        id: "country",
        header: "Country",
        accessorKey: "document.country",
        enableSorting: false,
        size: 140,
        meta: { filterType: "faceted-select" },
      },
      enumColumn(
        "gender",
        "Gender",
        "gender",
        options("Men", "Women", "Mixed"),
      ),
      {
        id: "city",
        header: "City",
        accessorKey: "document.city",
        enableSorting: false,
        size: 140,
        enableColumnFilter: false,
      },
      {
        id: "homeVenueId",
        header: "Home stadium",
        accessorKey: "document.homeVenueId",
        enableSorting: false,
        enableColumnFilter: false,
        size: 120,
        cell: ({ getValue }: any) => (getValue() ? "set" : "—"),
      },
      {
        ...enumColumn(
          "status",
          "Status",
          "status",
          options("Draft", "Complete", "Archived"),
          "select",
        ),
        cell: function render({ getValue }: any) {
          const status = getValue();
          return status ? <Badge variant="default">{status}</Badge> : null;
        },
      },
      {
        ...enumColumn("hide", "Hidden", "hide", booleanOptions, "select"),
        cell: ({ getValue }: any) =>
          getValue() ? <Badge variant="secondary">Hidden</Badge> : null,
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enablePinning: true,
        size: 100,
        cell: function render({ row }) {
          return (
            <div className="flex items-center gap-1">
              <ShowButton
                size="sm"
                hideText
                recordItemId={row.original.document.id}
              />
              <EditButton
                size="sm"
                hideText
                recordItemId={row.original.document.id}
              />
            </div>
          );
        },
      },
    ],
    [],
  );
